import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Settings2, ExternalLink, Loader2 } from 'lucide-react'

interface TenantSummary {
  id: string
  name: string
  operator: string
  operator_short: string
  domain: string
  branding: { primary: string; logo: string }
  features: Record<string, boolean>
  users: { total: number; admins: number }
}

interface PortalStats {
  tenants: { total: number; list: { id: string; name: string; user_count: number }[] }
  users: { total: number; admins: number }
}

export function PortalDashboard() {
  const [tenants, setTenants] = useState<TenantSummary[]>([])
  const [stats, setStats] = useState<PortalStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch('/api/portal/tenants').then(r => r.ok ? r.json() : null),
      fetch('/api/portal/stats').then(r => r.ok ? r.json() : null),
    ]).then(([tenantData, statsData]) => {
      if (tenantData?.tenants) setTenants(tenantData.tenants)
      if (statsData) setStats(statsData)
    }).finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
      </div>
    )
  }

  const clientTenants = tenants.filter(t => t.id !== 'magic-metrics')

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-ink-dark">Magic Metrics Portal</h1>
        <p className="text-sm text-ink-soft mt-1">
          Manage copilots, users, and feature access across all operators.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Active Copilots" value={clientTenants.length} />
          <StatCard label="Total Users" value={stats.users.total} />
          <StatCard label="Admins" value={stats.users.admins} />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-ink-dark">Client Copilots</h2>
          <Link
            to="/portal/users"
            className="text-sm font-medium text-brand-600 hover:underline flex items-center gap-1"
          >
            <Users className="w-4 h-4" />
            All Users
          </Link>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {clientTenants.map(t => (
            <TenantCard key={t.id} tenant={t} />
          ))}

          <Link
            to="/portal/provision"
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-6 text-sm font-medium text-ink-soft hover:border-brand-400 hover:text-brand-600 transition-colors"
          >
            <span className="text-2xl">+</span>
            New Copilot
          </Link>
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 p-5">
      <div className="text-2xl font-bold text-ink-dark">{value}</div>
      <div className="text-xs font-medium text-ink-soft uppercase tracking-wider mt-1">{label}</div>
    </div>
  )
}

function TenantCard({ tenant }: { tenant: TenantSummary }) {
  const featureCount = Object.values(tenant.features).filter(Boolean).length
  const totalFeatures = Object.keys(tenant.features).length

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 p-5 space-y-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: tenant.branding.primary }}
          >
            {tenant.operator_short?.[0] || tenant.name[0]}
          </div>
          <div>
            <div className="font-semibold text-ink-dark text-sm">{tenant.name}</div>
            <div className="text-xs text-ink-soft">{tenant.operator}</div>
          </div>
        </div>
        <a
          href={`https://${tenant.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink-soft hover:text-brand-600 transition-colors"
          title={`Open ${tenant.domain}`}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="flex items-center gap-4 text-xs text-ink-soft">
        <span className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5" />
          {tenant.users.total} users
        </span>
        <span className="flex items-center gap-1">
          <Settings2 className="w-3.5 h-3.5" />
          {featureCount}/{totalFeatures} features
        </span>
      </div>

      <div className="flex gap-2">
        <Link
          to={`/portal/tenants/${tenant.id}`}
          className="flex-1 text-center text-xs font-medium px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors"
        >
          Manage
        </Link>
        <Link
          to={`/portal/tenants/${tenant.id}/features`}
          className="text-xs font-medium px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Features
        </Link>
      </div>
    </div>
  )
}
